import React from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient } from '@apollo/client';
import * as S from './UserSummaryCard.styled';
import { CopyButton } from '@/components/CopyButton';
import { formatNumber, formatPhoneNumber } from '@/utils/helpers';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';
import { useCurrentUser } from '@/features/Profile/core/hooks';
import { AddBalance } from '@/components/Payment/AddBalance';
import NoSsr from '@/components/NoSsr/NoSsr';
import { WarningModal } from '@/components/Ad/WarningModal';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { QUERY } from '@/constants/common';
import { ROUTES } from '@/constants/routes';
import { GET_BALANCE } from '@/features/Profile/sections/Balance/api';

type UserSummaryCardProps = {
  currentPath: string;
};

export const UserSummaryCard = ({ currentPath }: UserSummaryCardProps) => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { isShop, isActiveShop, loading, shop, user } = useCurrentUser();
  const [isAddBalanceOpen, toggleAddBalance] = useToggleOpenState(
    QUERY.replenishment
  );
  const [isSuccessPopupOpen, toggleSuccessPopup] = useToggleOpenState(false);

  const { hasLogo, hasShopName } = user.features?.servicesFeatures ?? {};
  const name = shop && isActiveShop && hasShopName ? shop.name : user.name;
  const avatarUrl =
    shop && isActiveShop && hasLogo ? shop.logo : user.avatarUrl;
  const {
    id,
    phone,
    email,
    emailConfirmed,
    walletBalance,
    isAppleGooglePayEnabled,
    paymentMethods,
  } = user;

  function openSuccessPopup() {
    toggleSuccessPopup(true);
  }

  return (
    <>
      <S.Card $hasBottomMargin={isShop}>
        <S.Avatar $avatarUrl={avatarUrl} />
        <S.UserInfoContainer>
          {name && <S.UserName>{name}</S.UserName>}
          {id && (
            <S.Meta as="div">
              <S.UserId>
                ID: {id}
                <CopyButton
                  value={id}
                  successText={t('pages.cabinet.profile_number_copied')}
                />
              </S.UserId>
            </S.Meta>
          )}
          {phone && (
            <S.Meta>
              {t('pages.cabinet.tel')} {formatPhoneNumber(phone)}
            </S.Meta>
          )}
        </S.UserInfoContainer>

        {currentPath !== ROUTES.settings && (
          <S.EmailConfirmationLinkBox
            email={email}
            emailConfirmed={emailConfirmed}
          />
        )}
      </S.Card>

      {!isShop && (
        <S.Balance>
          <div>
            <S.BalanceLabel>
              {t('pages.cabinet.private_account')}
            </S.BalanceLabel>
            {loading ? (
              <SkeletonLoader width={80} height={22} borderRadius={7} />
            ) : (
              <S.BalanceValue>
                {formatNumber(walletBalance, 2).replace(/\s/g, '')} ₼
              </S.BalanceValue>
            )}
          </div>
          <S.BalanceButton
            data-stat="profile-add-balance"
            appearance="secondary"
            borderRadius={14}
            onClick={() => toggleAddBalance()}
          >
            {t('pages.cabinet.add')}
          </S.BalanceButton>
        </S.Balance>
      )}

      <NoSsr>
        <AddBalance
          isOpen={isAddBalanceOpen}
          toggle={toggleAddBalance}
          isAppleGooglePayEnabled={isAppleGooglePayEnabled}
          onSuccess={openSuccessPopup}
          paymentMethods={paymentMethods}
          isInPortal
          {...(isShop && {
            onBalanceTopUpSuccess: () => {
              client.refetchQueries({ include: [GET_BALANCE] });
            },
          })}
        />
      </NoSsr>

      <WarningModal
        dataTestId={AdPageTestIds.popup.success}
        isOpen={isSuccessPopupOpen}
        onClose={() => toggleSuccessPopup(false)}
        icon="check-circle-rounded.svg"
        iconSize={30}
        title={t('pages.cabinet.add_balance_success_message')}
        description=""
      />
    </>
  );
};
